import { getPref, setPref } from '../persistentPrefs';
import { isPhoneLayout } from '../platform';
import { k } from '../../localization/t';

const STORAGE_KEY = 'fab-graphics-settings';

// Render scale presets. `factor` scales the baseline pixel ratio used by
// the WebGL renderer. `high` matches the legacy default. `low`/`medium`
// reduce GPU cost for weaker hardware; `ultra` super-samples for sharper
// visuals on capable machines.
export const RESOLUTION_PRESETS = {
  low: { label: k('Low'), factor: 0.5 },
  medium: { label: k('Medium'), factor: 0.75 },
  high: { label: k('High'), factor: 1.0 },
  ultra: { label: k('Ultra'), factor: 1.5 },
};

const defaults = {
  resolution: 'high',
  brightness: 1.0,
  ssao: true,
  bloom: true,
  vignette: true,

  antialiasing: true,
  candleLight: true,
  preset: 'high',
};

// Quality presets. Each defines resolution + which post-processing
// effects are enabled. Players pick a preset for a quick one-click
// config, then can fine-tune individual toggles (which clears the
// preset indicator to "Custom").
export const QUALITY_PRESETS = {
  low: {
    label: k('Low'),
    desc: k('Best performance. Integrated GPUs, older laptops'),
    resolution: 'low',
    ssao: false,
    bloom: false,
    vignette: false,
  
    antialiasing: false,
    candleLight: false,
  },
  medium: {
    label: k('Medium'),
    desc: k('Balanced. Modern laptops, light desktops'),
    resolution: 'medium',
    ssao: false,
    bloom: true,
    vignette: true,
  
    antialiasing: true,
    candleLight: true,
  },
  high: {
    label: k('High'),
    desc: k('Full quality. Desktops, dedicated GPUs'),
    resolution: 'high',
    ssao: true,
    bloom: true,
    vignette: true,
  
    antialiasing: true,
    candleLight: true,
  },
  ultra: {
    label: k('Ultra'),
    desc: k('Maximum quality. High-end hardware'),
    resolution: 'ultra',
    ssao: true,
    bloom: true,
    vignette: true,
  
    antialiasing: true,
    candleLight: true,
  },
};

export function applyPreset(presetKey) {
  const preset = QUALITY_PRESETS[presetKey];
  if (!preset) return;
  saveGraphicsSettings({
    preset: presetKey,
    resolution: preset.resolution,
    ssao: preset.ssao,
    bloom: preset.bloom,
    vignette: preset.vignette,

    antialiasing: preset.antialiasing,
    candleLight: preset.candleLight,
  });
}

// Detect whether current settings match any preset, or are custom.
export function detectPreset(settings) {
  for (const [key, preset] of Object.entries(QUALITY_PRESETS)) {
    if (settings.resolution === preset.resolution
      && (settings.ssao !== false) === preset.ssao
      && (settings.bloom !== false) === preset.bloom
      && (settings.vignette !== false) === preset.vignette

      && (settings.antialiasing !== false) === preset.antialiasing
      && (settings.candleLight !== false) === preset.candleLight) {
      return key;
    }
  }
  return null;
}

let cached = null;
const listeners = new Set();

export function getGraphicsSettings() {
  if (cached) return cached;
  const stored = getPref(STORAGE_KEY, null);
  cached = stored && typeof stored === 'object' ? { ...defaults, ...stored } : { ...defaults };
  if (!RESOLUTION_PRESETS[cached.resolution]) cached.resolution = defaults.resolution;
  return cached;
}

export function saveGraphicsSettings(patch) {
  cached = { ...getGraphicsSettings(), ...patch };
  setPref(STORAGE_KEY, cached);
  for (const fn of listeners) fn(cached);
}

export function onGraphicsChange(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

// `high` returns the legacy `min(devicePixelRatio, 1.5)` baseline. Other
// presets scale that baseline so the relationship to the user's display
// is preserved across hardware (a retina screen on `low` still beats a
// non-retina screen on `high`).
export function getEffectivePixelRatio() {
  const preset = RESOLUTION_PRESETS[getGraphicsSettings().resolution] || RESOLUTION_PRESETS.high;
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  // Phones get a tighter baseline: framebuffer memory scales with the square
  // of this value and counts against iOS Safari's per-tab memory budget.
  const baseline = Math.min(dpr, isPhoneLayout() ? 1.25 : 1.5);
  return baseline * preset.factor;
}

// Apply the brightness setting as a CSS filter on <html>. Using the root
// element means the filter covers BOTH the Three.js canvas AND the 2D
// Preact UI uniformly. On the root element, CSS spec guarantees that
// `position: fixed` descendants still reference the viewport (unlike
// any other element, where `filter` creates a new containing block).
// This is the standard approach for game brightness sliders in web/CEF
// environments. The GPU compositor handles it with zero CPU cost.
export function applyBrightness() {
  if (typeof document === 'undefined') return;
  const brightness = getGraphicsSettings().brightness ?? 1.0;
  // At exactly 1.0, remove the filter entirely so there's no compositor
  // overhead for the default/unmodified case.
  if (brightness === 1.0) {
    document.documentElement.style.filter = '';
  } else {
    document.documentElement.style.filter = `brightness(${brightness})`;
  }
}

